import axios from 'axios';
import { API_BASE_URL } from './apiConfig';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Dedicated Critical Minerals Dashboard API endpoints
export const getCriticalMineralsDashboardData = async (mineral, year, metric) => {
  try {
    const response = await api.get('/api/critical-minerals/dashboard', {
      params: { mineral, year, metric }
    });
    
    if (!response.data) {
      throw new Error('Failed to fetch critical minerals dashboard data');
    }
    
    return response.data;
    
  } catch (error) {
    console.error('Error fetching critical minerals dashboard data:', error);
    // Return empty data structure if API fails
    return {
      overallRisk: 0,
      geojson: { type: "FeatureCollection", features: [] },
      countries: [],
      dataSource: "Error: Failed to fetch data from API"
    };
  }
};

// Get country detail information from critical minerals API
export const getCountryDetail = async (countryName, mineral, year) => {
  try {
    const response = await api.get(`/api/critical-minerals/country/${encodeURIComponent(countryName)}`, {
      params: { mineral, year }
    });
    
    if (!response.data) {
      throw new Error('Failed to fetch country detail data');
    }
    
    return response.data;
    
  } catch (error) {
    console.error('Error fetching country detail:', error);
    // Return empty data structure if API fails
    return {
      dependencyRatio: 0,
      wgiPoliticalStability: 0,
      fsi: 0,
      kabilMous: 'No data available',
      recentAlerts: 'No recent alerts',
      top3Partners: 'No partners identified'
    };
  }
};

// Get live alerts for a specific mineral from critical minerals API
export const getLiveAlerts = async (mineral) => {
  try {
    const response = await api.get('/api/critical-minerals/alerts', {
      params: { mineral }
    });
    
    if (!response.data) {
      throw new Error('Failed to fetch live alerts data');
    }
    
    // Transform the alerts to include PESTLE categories and better formatting
    const transformedAlerts = response.data.map(alert => ({
      date: alert.date || alert.created_at || 'Recent',
      message: alert.message || alert.alert_message || alert.title || 'Risk Alert',
      severity: alert.severity || alert.risk_level || 'Medium',
      pestle_risk_level: alert.pestle_risk_level || alert.severity || 'Medium',
      source: alert.source || 'Database',
      type: alert.type || alert.alert_type || 'S',
      // Add PESTLE category based on alert type
              pestleCategory: alert.pestleCategory || getPestleCategory(alert.type || alert.alert_type || 'S'),
      // Add source URL if available (for linking to original article)
      sourceUrl: alert.sourceUrl || alert.source_url || alert.url || null,
      sourceName: alert.source_name || 'Database'
    }));
    
    return transformedAlerts;
    
     } catch (error) {
     console.error('Error fetching live alerts:', error);
     // Return empty array - no alerts available
     return [];
   }
};

// Get trade restrictions data for supply chain analysis
export const getTradeRestrictionsData = async (mineral, year, restrictionType) => {
  try {
    const response = await api.get('/api/critical-minerals/trade-restrictions', {
      params: { mineral, year, restriction_type: restrictionType }
    });
    
    if (!response.data) {
      throw new Error('Failed to fetch trade restrictions data');
    }
    
    return response.data;
    
  } catch (error) {
    console.error('Error fetching trade restrictions data:', error);
    // Return empty data structure if API fails
    return {
      success: false,
      data: {
        countries: [],
        summary: {
          totalCountries: 0,
          mineral: mineral,
          year: year,
          restrictionType: restrictionType,
          dataSource: "Error: Failed to fetch data from API"
        },
        restrictionTypes: [],
        colorMapping: {},
        lastUpdated: new Date().toISOString()
      }
    };
  }
};

// Get available years for a specific mineral and metric
export const getAvailableYears = async (mineral, metric = 'Import Ratio') => {
  try {
    const response = await api.get('/api/critical-minerals/available-years', {
      params: { mineral, metric }
    });
    
    if (!response.data) {
      throw new Error('Failed to fetch available years');
    }
    
    return response.data;
    
  } catch (error) {
    console.error('Error fetching available years:', error);
    // Return fallback years - last 5 years dynamically calculated
    const currentYear = new Date().getFullYear();
    const last5Years = [];
    for (let i = 0; i < 6; i++) {
      last5Years.push((currentYear - i).toString());
    }
    
    return {
      years: last5Years,
      latest_year: last5Years[0],
      partner_risk_years: last5Years,
      dependency_years: last5Years,
      metric: metric
    };
  }
};

// Enhanced function to get database alerts with better error handling
export const getDatabaseAlerts = async (mineral, metric = null) => {
  try {
    console.log(`Fetching database alerts for mineral: ${mineral}, metric: ${metric}`);
    
    // Try to get alerts from the critical minerals alerts endpoint
    const response = await api.get('/api/critical-minerals/alerts', {
      params: { 
        mineral,
        ...(metric && { metric })
      }
    });
    
    console.log('Raw API response:', response);
    console.log('Response data:', response.data);
    console.log('Response data type:', typeof response.data);
    console.log('Response data length:', Array.isArray(response.data) ? response.data.length : 'Not an array');
    
    // Handle different response formats
    let alertsData = [];
    
    if (response.data) {
      if (Array.isArray(response.data)) {
        alertsData = response.data;
      } else if (response.data.alerts && Array.isArray(response.data.alerts)) {
        alertsData = response.data.alerts;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        alertsData = response.data.data;
      } else if (typeof response.data === 'object') {
        // If it's a single alert object, wrap it in an array
        alertsData = [response.data];
      }
    }
    
    console.log('Processed alerts data:', alertsData);
    
    if (alertsData.length > 0) {
      const transformedAlerts = alertsData.map(alert => {
        console.log('Processing alert:', alert);
        
        const transformed = {
          date: alert.date || alert.created_at || alert.published_date || alert.timestamp || 'Recent',
          message: alert.message || alert.alert_message || alert.title || alert.summary || alert.content || 'Risk Alert',
          severity: alert.severity || alert.risk_level || alert.alert_level || alert.priority || 'Medium',
          pestle_risk_level: alert.pestle_risk_level || alert.severity || 'Medium',
          source: alert.source || 'Database',
          type: alert.type || alert.alert_type || alert.category || alert.pestle_category || 'S',
          pestleCategory: getPestleCategory(alert.type || alert.alert_type || alert.category || alert.pestle_category || 'S'),
          sourceUrl: alert.sourceUrl || alert.source_url || alert.url || alert.article_url || alert.link || null,
          sourceName: alert.source_name || alert.publisher || alert.source || 'Database'
        };
        
        console.log('Transformed alert:', transformed);
        return transformed;
      });
      
      console.log('Final transformed alerts:', transformedAlerts);
      return transformedAlerts;
    }
    
    console.log('No alerts data found, trying strategic exposure as fallback');
    
    // Try strategic exposure as fallback
    try {
      console.log('Attempting strategic exposure fallback...');
      const strategicResponse = await api.get('/api/strategic-exposure');
      const strategicData = strategicResponse.data;
      
      if (strategicData.success && strategicData.data && strategicData.data.alerts) {
        console.log('Strategic exposure data found:', strategicData.data.alerts);
        
        // Filter alerts for the specific mineral
        const mineralAlerts = strategicData.data.alerts.filter(alert => 
          alert.mineral && alert.mineral.toLowerCase().includes(mineral.toLowerCase())
        );
        
        if (mineralAlerts.length > 0) {
          const transformedAlerts = mineralAlerts.map(alert => ({
            date: alert.recent_articles?.[0]?.date || 'Recent',
            message: alert.alert_type || 'Risk Alert',
            severity: alert.risk_level === 'Critical' ? 'High' : 
                     alert.risk_level === 'High' ? 'Medium' : 'Low',
            pestle_risk_level: alert.risk_level || 'Medium',
            source: 'Strategic Analysis',
            type: 'S',
            pestleCategory: 'Social',
            sourceUrl: null,
            sourceName: 'Strategic Analysis'
          }));
          
          console.log('Fallback alerts from strategic exposure:', transformedAlerts);
          return transformedAlerts;
        }
      }
      
             // If no alerts, return empty array instead of creating dummy alerts
       console.log('No alerts found in strategic exposure data');
    } catch (strategicError) {
      console.log('Strategic exposure fallback also failed:', strategicError);
    }
    
         // If all else fails, return empty array - no alerts available
     console.log('No data available, returning empty array');
     return [];
    
  } catch (error) {
    console.error('Error fetching database alerts:', error);
    console.error('Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    
         // Return empty array on error - no alerts available
     return [];
  }
};

// Helper function to determine PESTLE category based on alert type
const getPestleCategory = (alertType) => {
  const pestleMapping = {
    'P': 'political',      // Political alerts
    'E': 'economic',       // Economic alerts  
    'S': 'social',         // Social/Security alerts
    'T': 'technological',  // Technological alerts
    'L': 'legal',          // Legal alerts
    'EN': 'environmental'  // Environmental alerts
  };
  
  return pestleMapping[alertType] || 'economic';
};

// Enhanced function to get mineral-specific news articles for Live Alerts
export const getMineralSpecificNews = async (mineral, metric = 'Import Ratio') => {
  try {
    // First try to get real data from the critical minerals alerts endpoint
    const alertsResponse = await api.get('/api/critical-minerals/alerts', {
      params: { mineral, metric }
    });
    
    if (alertsResponse.data && Array.isArray(alertsResponse.data)) {
      // Transform the real database alerts to match our frontend format
      const transformedAlerts = alertsResponse.data.map(alert => ({
        date: alert.date || alert.created_at || 'Recent',
        message: alert.message || alert.alert_message || alert.title || 'Risk Alert',
        severity: alert.severity || alert.risk_level || 'Medium',
        pestle_risk_level: alert.pestle_risk_level || alert.severity || 'Medium',
        source: alert.source || 'Database',
        type: alert.type || alert.alert_type || 'S',
        pestleCategory: alert.pestleCategory || getPestleCategory(alert.type || alert.alert_type || 'S'),
        sourceUrl: alert.sourceUrl || alert.source_url || alert.url || null,
        sourceName: alert.source_name || 'Database'
      }));
      
      // If we have real data, return it
      if (transformedAlerts.length > 0) {
        return transformedAlerts;
      }
    }
    
    // Fallback: Try to get data from strategic exposure endpoint
    try {
      const response = await api.get('/api/strategic-exposure');
      const data = response.data;
      
      if (data.success && data.data && data.data.mineralData) {
        // Map mineral names to strategic exposure keys
        const mineralMapping = {
          'lithium': 'lithium',
          'cobalt': 'cobalt',
          'nickel': 'nickel',
          'rareearth': 'rareearth',
          'rare earth elements': 'rareearth',
          'graphite': 'graphite',
          'manganese': 'manganese'
        };
        
        const mineralKey = mineralMapping[mineral.toLowerCase().replace(/\s+/g, '')] || 
                          mineral.toLowerCase().replace(/\s+/g, '');
        const mineralData = data.data.mineralData[mineralKey];
        
        if (mineralData) {
          const alerts = [];
          
          // Check if there are alerts for this mineral
          if (data.data.alerts && Array.isArray(data.data.alerts)) {
            data.data.alerts.forEach(alert => {
              if (alert.mineral && alert.mineral.toLowerCase().includes(mineral.toLowerCase())) {
                alerts.push({
                  date: alert.recent_articles?.[0]?.date || 'Recent',
                  message: alert.alert_type || 'Risk Alert',
                  severity: alert.risk_level === 'Critical' ? 'High' : 
                           alert.risk_level === 'High' ? 'Medium' : 'Low',
                  pestle_risk_level: alert.risk_level || 'Medium',
                  source: 'Strategic Analysis',
                  type: 'S',
                  pestleCategory: getPestleCategory('S'),
                  sourceUrl: null,
                  sourceName: 'Strategic Analysis'
                });
              }
            });
          }
          
                     // If no specific alerts, return empty array instead of creating dummy alerts
           console.log('No alerts found, returning empty array');
          
          return alerts;
        }
      }
    } catch (strategicError) {
      console.log('Strategic exposure endpoint not available, continuing with fallback');
    }
    
    // If no real data available, return empty array instead of dummy data
    return [];
    
  } catch (error) {
    console.error('Error fetching mineral-specific news:', error);
    // Return empty array instead of dummy data
    return [];
  }
};

// Legacy functions for backward compatibility
export const getCriticalMineralsData = async (mineral, year, metric) => {
  // Use the new dedicated endpoint
  return getCriticalMineralsDashboardData(mineral, year, metric);
};

// Test function to debug API endpoints
export const testApiEndpoints = async () => {
  console.log('=== TESTING API ENDPOINTS ===');
  
  try {
    // Test critical minerals alerts endpoint
    console.log('Testing /api/critical-minerals/alerts...');
    const alertsResponse = await api.get('/api/critical-minerals/alerts', {
      params: { mineral: 'Lithium' }
    });
    console.log('Critical minerals alerts response:', alertsResponse.data);
  } catch (error) {
    console.error('Critical minerals alerts endpoint failed:', error.response?.status, error.response?.data);
  }
  
  try {
    // Test strategic exposure endpoint
    console.log('Testing /api/strategic-exposure...');
    const strategicResponse = await api.get('/api/strategic-exposure');
    console.log('Strategic exposure response:', strategicResponse.data);
  } catch (error) {
    console.error('Strategic exposure endpoint failed:', error.response?.status, error.response?.data);
  }
  
  try {
    // Test critical minerals dashboard endpoint
    console.log('Testing /api/critical-minerals/dashboard...');
    const dashboardResponse = await api.get('/api/critical-minerals/dashboard', {
      params: { mineral: 'Lithium', year: '2024', metric: 'Import Ratio' }
    });
    console.log('Dashboard response:', dashboardResponse.data);
  } catch (error) {
    console.error('Dashboard endpoint failed:', error.response?.status, error.response?.data);
  }
  
  console.log('=== END API TESTING ===');
};

// Get import trends data for multiple minerals across multiple years
export const getImportTrends = async (minerals = "Lithium,Cobalt,Nickel,Rare Earth Elements,Graphite", years = "2020,2021,2022,2023,2024") => {
  try {
    const response = await api.get('/api/critical-minerals/import-trends', {
      params: { minerals, years }
    });
    
    if (!response.data) {
      throw new Error('Failed to fetch import trends data');
    }
    
    return response.data;
    
  } catch (error) {
    console.error('Error fetching import trends:', error);
    // Return fallback data structure if API fails
    return {
      success: false,
      data: [],
      unit: 'Million Tonnes',
      source: 'Error: Failed to fetch data from API',
      minerals: minerals.split(',').map(m => m.trim()),
      years: years.split(',').map(y => parseInt(y.trim()))
    };
  }
};

// Get mineral sector impact data with industry details and primary industry assignments
export const getMineralSectorImpact = async (mode = 'import-distribution', year = null, mineral = null) => {
  try {
    console.log('Fetching mineral sector impact data from /api/mineral-sector-impact');
    
    const params = { mode };
    if (year) params.year = year;
    if (mineral) params.mineral = mineral;
    
    const response = await api.get('/api/mineral-sector-impact', { params });
    
    console.log('Mineral sector impact API response:', response);
    console.log('Response data:', response.data);
    console.log('Response status:', response.status);
    
    if (!response.data) {
      throw new Error('Failed to fetch mineral sector impact data - no data in response');
    }
    
    // Check if the response has the expected structure
    if (response.data.success === false) {
      console.log('API returned success: false');
      return response.data;
    }
    
    // If data is directly in response.data, wrap it properly
    if (Array.isArray(response.data)) {
      return {
        success: true,
        data: response.data,
        count: response.data.length
      };
    }
    
    return response.data;
    
  } catch (error) {
    console.error('Error fetching mineral sector impact data:', error);
    console.error('Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      url: error.config?.url
    });
    
    // Return error structure if API fails
    return {
      success: false,
      data: [],
      count: 0,
      error: error.message
    };
  }
};

// Get available years and minerals for mineral sector impact filtering
export const getMineralSectorImpactAvailableData = async () => {
  try {
    console.log('Fetching available data from /api/mineral-sector-impact/available-data');
    const response = await api.get('/api/mineral-sector-impact/available-data');
    
    if (!response.data) {
      throw new Error('Failed to fetch available data');
    }
    
    return response.data;
    
  } catch (error) {
    console.error('Error fetching available data:', error);
    
    // Return fallback data
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i < 5; i++) {
      years.push((currentYear - i).toString());
    }
    
    return {
      success: true,
      years: years,
      minerals: ['All', 'Lithium', 'Cobalt', 'Nickel', 'Rare Earth Elements', 'Graphite'],
      timestamp: new Date().toISOString()
    };
  }
};

// ECI Ranking API functions
export const getECIRankingData = async (view = 'top-10', mineral = 'Lithium', mode = 'rank') => {
  try {
    const response = await api.get('/api/eci-ranking', {
      params: { view, mineral, mode }
    });
    
    if (!response.data) {
      throw new Error('Failed to fetch ECI ranking data');
    }
    
    return response.data;
    
  } catch (error) {
    console.error('Error fetching ECI ranking data:', error);
    // Return mock data if API fails
    return {
      data: [],
      summary: {
        totalCountries: 0,
        yearRange: '2020-2023',
        complexityRange: '-2.0 to 2.5'
      }
    };
  }
};

export const getECICountryDetails = async (country) => {
  try {
    const response = await api.get(`/api/eci-ranking/country/${encodeURIComponent(country)}`);
    
    if (!response.data) {
      throw new Error('Failed to fetch ECI country details');
    }
    
    return response.data;
    
  } catch (error) {
    console.error('Error fetching ECI country details:', error);
    return {
      country: country,
      currentRank: null,
      currentECI: null,
      trend: 'stable',
      minerals: [],
      complexity_level: 'Unknown'
    };
  }
};

export default api;
